/**
 * Tiny fetch wrapper that:
 *  - adds the Bearer access token from the in-memory token store
 *  - sends credentials so the refresh cookie is included
 *  - parses JSON responses and surfaces typed errors
 *  - on 401, attempts a one-shot refresh and replays the request
 */

import { getAccessToken, setAccessToken } from "./token-store";

export interface ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
}

/**
 * Flatten an API error into a list of user-facing messages. Handles the
 * shape of Zod's flatten() output that the server returns on 400s, plus
 * plain HttpError messages and unknown errors.
 *
 * Always returns at least one message.
 */
export function formatApiError(err: unknown): string[] {
  const messages: string[] = [];

  if (typeof err === "object" && err !== null) {
    const apiErr = err as ApiError;
    const details = apiErr.details;

    if (details && typeof details === "object") {
      const d = details as { formErrors?: string[]; fieldErrors?: Record<string, string[]> };
      if (Array.isArray(d.formErrors)) messages.push(...d.formErrors);
      if (d.fieldErrors && typeof d.fieldErrors === "object") {
        for (const [field, fieldMessages] of Object.entries(d.fieldErrors)) {
          if (Array.isArray(fieldMessages)) {
            for (const msg of fieldMessages) {
              messages.push(`${humanFieldName(field)}: ${msg}`);
            }
          }
        }
      }
    }

    if (messages.length === 0 && typeof apiErr.message === "string") {
      messages.push(apiErr.message);
    }
  } else if (typeof err === "string") {
    messages.push(err);
  }

  if (messages.length === 0) messages.push("Something went wrong. Please try again.");
  return messages;
}

const FIELD_NAMES: Record<string, string> = {
  fullName: "Full name",
  email: "Email",
  password: "Password",
  organisationName: "Organisation name",
  organisationSlug: "URL slug",
};

const humanFieldName = (key: string): string =>
  FIELD_NAMES[key] ??
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

const buildUrl = (path: string): string => {
  const base = import.meta.env.VITE_API_URL ?? "/api/v1";
  return `${base.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;
};

const parseJsonSafe = async (res: Response): Promise<unknown> => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
};

const toApiError = (status: number, body: unknown): ApiError => {
  const fallback = `Request failed with status ${status}`;
  let message = fallback;
  let code: string | undefined;
  let details: unknown;
  if (body && typeof body === "object" && "error" in body) {
    const errObj = (body as { error?: { message?: string; code?: string; details?: unknown } })
      .error;
    if (errObj?.message) message = errObj.message;
    code = errObj?.code;
    details = errObj?.details;
  }
  const err = new Error(message) as ApiError;
  err.status = status;
  err.code = code;
  err.details = details;
  return err;
};

const tryRefresh = async (): Promise<boolean> => {
  try {
    const res = await fetch(buildUrl("/auth/refresh"), {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) return false;
    const body = (await res.json()) as { accessToken?: string };
    if (typeof body.accessToken === "string") {
      setAccessToken(body.accessToken);
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

export interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  skipAuth?: boolean;
}

const isFormDataBody = (body: unknown): body is FormData =>
  typeof FormData !== "undefined" && body instanceof FormData;

async function apiOnce<T>(path: string, options: ApiOptions): Promise<T> {
  const headers = new Headers(options.headers);
  const bodyIsFormData = isFormDataBody(options.body);

  // For JSON bodies we set Content-Type ourselves; for FormData we let the
  // browser set it (so the multipart boundary is correct). The Authorisation
  // header is added below in either case.
  if (options.body !== undefined && !bodyIsFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!options.skipAuth) {
    const token = getAccessToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }

  let bodyInit: BodyInit | undefined;
  if (options.body === undefined) {
    bodyInit = undefined;
  } else if (bodyIsFormData) {
    bodyInit = options.body as FormData;
  } else {
    bodyInit = JSON.stringify(options.body);
  }

  const init: RequestInit = {
    method: options.method ?? "GET",
    headers,
    credentials: "include",
    body: bodyInit,
  };

  const res = await fetch(buildUrl(path), init);
  if (res.status === 204) return undefined as T;
  const body = await parseJsonSafe(res);
  if (!res.ok) throw toApiError(res.status, body);
  return body as T;
}

export async function api<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  try {
    return await apiOnce<T>(path, options);
  } catch (err) {
    const apiErr = err as ApiError;
    if (apiErr.status === 401 && !options.skipAuth && path !== "/auth/refresh") {
      const refreshed = await tryRefresh();
      if (refreshed) {
        return apiOnce<T>(path, options);
      }
    }
    throw err;
  }
}
